import isNumber from "lodash/isNumber"
import keys from "lodash/keys"
import forEach from "lodash/forEach"
import toLower from "lodash/toLower"
import map from "lodash/map"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import isArray from "lodash/isArray"
import React, { useState, useEffect } from "react"
import { AuthoringUtils } from "@adobe/aem-spa-page-model-manager"
import { AuthenticationContext } from "contexts-lib/AuthenticationContext"
import { userType } from "constants-lib/authentication"
import { connect } from "react-redux"
import { getUserState } from "utils-lib/userStates"
import { isMatchedUserState } from "utils-lib/authorTools/userState"
import {
  USER_STATE_TO_AEM_STATE_MAPPING,
  COMPONENT_PATH_MAP,
} from "constants-lib/userState"
import { localStorageKey } from "constants-lib/localStorage"
import { getWindow } from "utils-lib/getWindow"
import { isNextApp } from "utils-lib/isNextApp"

const ssrWindow = getWindow()

const isTrue = (value) => toLower(value) === `true` || value === true

/**
 * Sets the user state in local storage.
 * @param {Object} options - The options object.
 * @param {string} options.currentUserState - The current user state.
 * @param {string} options.userStatesAndLobs - The user states and LOBs.
 */
function setLocalStorageUserState({ currentUserState, userStatesAndLobs }) {
  const mappedUserState = USER_STATE_TO_AEM_STATE_MAPPING[userStatesAndLobs]

  ssrWindow.localStorage.setItem(
    localStorageKey.APPLICATION_USER_STATE,
    JSON.stringify({
      currentUserState,
      mappedUserState,
      userStatesAndLobs,
    }),
  )
}

/**
 * A higher-order component that provides the user state to the wrapped component.
 *
 * @param {React.Component} WrappedComponent - The component to be wrapped.
 * @returns {React.Component} - A new component that renders the wrapped component with the user state.
 */
// eslint-disable-next-line react/display-name
export const withUserState = (WrappedComponent) => (props) =>
  (
    // ... and renders the wrapped component with the context language!
    // Notice that we pass through any additional props as well
    <AuthenticationContext.Consumer>
      {({ userState, logOut, isLoggedIn, userDetails, isAuthInitialized }) => (
        <WrappedComponent
          {...props}
          userState={userState}
          logOut={logOut}
          isLoggedIn={isLoggedIn}
          userDetails={userDetails}
          isAuthInitialized={isAuthInitialized}
        />
      )}
    </AuthenticationContext.Consumer>
  )

/**
 * Maps the state properties to the props of the component.
 *
 * @param {Object} state - The current state of the application.
 * @param {Object} state.customerSelections - The customer selections.
 * @param {Object} state.userManageAccount - The user manage account.
 * @param {Object} state.authorTools?.userState - The author tools user state.
 * @returns {Object} The props object.
 */
export const mapStateToProps = (state) => ({
  customerSelections: state.customerSelections,
  userManageAccount: state.userManageAccount,
  authorState: state.authorTools?.userState,
})

/**
 * Returns a boolean indicating whether the given `baseUserState` matches the `userState` in the `mapAEMState` object.
 * @param {Object} param - An object containing the `userState`.
 * @param {string} param.userState - The user state to match against.
 * @param {string} baseUserState - The base user state to match with.
 * @returns {boolean} - `true` if the `baseUserState` matches the `userState`, `false` otherwise.
 */
const getNonLOBStateMatch = ({ userState }, baseUserState) => {
  const matchedState = mapAEMState[userState]

  if (isArray(matchedState)) {
    return matchedState.indexOf(baseUserState) !== -1
  }

  return matchedState === baseUserState
}

/**
 * Determines whether the user's state matches the current state of the page.
 * @param {string} userStates - The user's state, as defined in their profile.
 * @param {string} extendedCurrentState - The current state of the user in AEM.
 * @returns {boolean} True if the user's state matches the current state of the page, false otherwise.
 */
const getLOBStateMatch = (userStates, extendedCurrentState) =>
  USER_STATE_TO_AEM_STATE_MAPPING[userStates] === extendedCurrentState

/**
 * Determines whether a component should be rendered based on the user's state and the current state of the page.
 * @param {Array<Object>} userStatesAndLobs - An array of objects that contain the user state and LOB data.
 * @param {string} baseUserState - The user state that is being compared against.
 * @param {string} extendedCurrentState - The current state of the user, which is a combination of the user's state and the LOBs they are in.
 * @param {string} type - The type of component being checked.
 * @returns {Array<boolean, number>} An array with two values. The first value is a boolean that indicates whether or not the component should be rendered. The second value is the index of the match.
 */
export const isMatchedState = (
  userStatesAndLobs,
  baseUserState,
  extendedCurrentState,
  type,
) => {
  let shouldRender = false
  let match = null

  for (let i = 0; i < userStatesAndLobs.length; i++) {
    const userStateArrayWithLobs = [userStatesAndLobs[i].userState]

    if (isTrue(userStatesAndLobs[i].isOmr)) userStateArrayWithLobs.push("omr")
    if (isTrue(userStatesAndLobs[i].isSmb)) userStateArrayWithLobs.push("smb")
    if (isTrue(userStatesAndLobs[i].isTro)) userStateArrayWithLobs.push("tro")

    /* there is no LOB data, continue the next iteration of the loop */
    // eslint-disable-next-line no-continue
    if (userStateArrayWithLobs.length === 1) continue

    shouldRender = getLOBStateMatch(
      userStateArrayWithLobs,
      extendedCurrentState,
    )

    /* if we found a match, break, otherwise continue the loop */
    if (shouldRender) {
      match = i
      break
    }
  }

  /* runs if no LOB matches were found */
  if (!shouldRender) {
    for (let i = 0; i < userStatesAndLobs.length; i++) {
      const userStateArrayWithLobs = [userStatesAndLobs[i].userState]

      if (isTrue(userStatesAndLobs[i].isOmr)) userStateArrayWithLobs.push("omr")
      if (isTrue(userStatesAndLobs[i].isSmb)) userStateArrayWithLobs.push("smb")
      if (isTrue(userStatesAndLobs[i].isTro)) userStateArrayWithLobs.push("tro")

      /* there is LOB data, continue the next iteration of the loop */
      if (type !== COMPONENT_PATH_MAP.ENTRY_AND_CONTACT) {
        // eslint-disable-next-line no-continue
        if (userStateArrayWithLobs.length > 1) continue
      }

      shouldRender = getNonLOBStateMatch(userStatesAndLobs[i], baseUserState)

      /* if we found a match, break, otherwise continue the loop */
      if (shouldRender) {
        match = i
        break
      }
    }
  }

  return [shouldRender, match]
}

/**
 * A mapping of AEM states to user types.
 * @type {Object<string, string|string[]>}
 */
const mapAEMState = {
  /**
   * User has not logged in before. Anonymous has been combined with prospective with the new AddressBarHero.
   * @type {string[]}
   */
  anonymous: [userType.ANONYMOUS, userType.PROSPECTIVE],
  /**
   * User who hasn't logged in, but has entered their address in an input field that we've cookied.
   * @type {string}
   */
  prospective: userType.PROSPECTIVE,
  /**
   * User who hasn't logged in, but has entered their address in an input field that we've cookied AND the address is serviced.
   * @type {string}
   */
  existing: userType.EXISTING,
  /**
   * Logged out user that now has their address cookied from their login account.
   * @type {string}
   */
  cookied: userType.COOKIED,
  /**
   * User who is currently logged in.
   * @type {string}
   */
  loggedIn: userType.LOGGED_IN,
}

/**
 * Wraps a component with authentication logic that only renders the component if the user state matches the
 * user states specified by the parent component/AEM author.
 * @param {React.ComponentType} WrappedComponent - The component to wrap with authentication logic.
 * @returns {React.ComponentType} A new component that only renders the original component if the user state matches.
 */
export const withAuthentication = (WrappedComponent) => {
  /**
   * A higher-order component that renders the wrapped component only if the user state matches the specified user states.
   * @param {Object} props - The props passed to the component.
   * @param {Object} props.authorState - The author state of the component.
   * @param {string} props.userState - The user state of the component.
   * @param {Array<Object>} props.userStatesAndLobs - The user states and LOBs of the component.
   * @param {Object} props.customerSelections - The customer selections of the component.
   * @param {Object} props.userManageAccount - The user manage account of the component.
   * @param {string} props.cqType - The cq type of the component.
   * @returns {React.ReactNode} The wrapped component if the user state matches, or null otherwise.
   */
  const WrappedComponentWithAuthentication = (props) => {
    const propsWithDefaults = {
      ...props,
      userStatesAndLobs: props.userStatesAndLobs || [],
      cqType: props.cqType || "",
      cmsContent: props.cmsContent || {},
      isAuthInitialized: props.isAuthInitialized || true,
    }

    const {
      authorState,
      userState,
      isAuthInitialized,
      userStatesAndLobs,
      customerSelections,
      userManageAccount,
      cqType: modelCqType,
      cmsContent: { cqType: prefCenterCqType },
    } = propsWithDefaults

    const [currentUserState, setCurrentUserState] = useState(``)

    // cqType is stored in a nested object for pref center
    const cqType = modelCqType || prefCenterCqType

    useEffect(() => {
      /* prevent issues when user clears address on another page */
      const getLinkedAccountStatus = get(
        userManageAccount,
        `getLinkedAccountStatus`,
        ``,
      )
      const getCombinedProductURLStatus = get(
        customerSelections,
        `getCombinedProductUrlStatus`,
        ``,
      )

      const getLinkedAccountsComplete =
        getLinkedAccountStatus && getLinkedAccountStatus !== `pending`

      const getCombinedProductUrlComplete =
        getCombinedProductURLStatus && getCombinedProductURLStatus !== `pending`

      /*
        The util depends on userManageAccount for loggedIn users and
        getCombinedProductUrlStatusComplete for guest users, so make sure at least
        one of these conditions is met
      */
      if (getLinkedAccountsComplete || getCombinedProductUrlComplete) {
        const newUserState = getUserState(customerSelections, userManageAccount)
        setCurrentUserState(newUserState)
      }
    }, [
      userManageAccount?.getLinkedAccountStatus,
      customerSelections?.getCombinedProductUrlStatus,
      userState,
    ])

    const isPrefCenterComponent =
      cqType === "wm/components/content/preferencesearch/loginform"

    // ssr loading is too fast, causes issues with userState logic in pref center
    if (isNextApp && isPrefCenterComponent && !isAuthInitialized) return null

    /*
      If the authored component has no user states to filter for, then show it.
    */
    if (isEmpty(userStatesAndLobs)) {
      return <WrappedComponent {...props} />
    }

    /*
      If the parent component/AEM author specified user states this component
      should be rendered for, then do not render this component if the current
      user state is not one of the ones supplied in the prop: userStates.
    */
    const isInStorybook = ssrWindow.location.host === "localhost:6006"
    if (AuthoringUtils.isInEditor() || isInStorybook) {
      const authorUserStatesAndLobs = map(
        userStatesAndLobs,
        ({ userState }) => userState,
      )

      forEach(userStatesAndLobs, ({ isOmr, isSmb, isTro }) => {
        if (isOmr) authorUserStatesAndLobs.push("omr")
        if (isSmb) authorUserStatesAndLobs.push("smb")
        if (isTro) authorUserStatesAndLobs.push("tro")
      })

      const shouldShow = isMatchedUserState(
        authorState,
        authorUserStatesAndLobs,
        cqType,
      )

      const stateToMatch =
        USER_STATE_TO_AEM_STATE_MAPPING[authorUserStatesAndLobs]

      let mappedCurrentUserState = ``
      if (!isEmpty(authorState)) {
        const authorUserStates = keys(authorState.journey)

        for (let i = 0; i < authorUserStates.length; i++) {
          const authorCurrentUserStatesAndLobs = [authorUserStates[i]]

          if (get(authorState, `lob.omr`, false))
            authorCurrentUserStatesAndLobs.push("omr")
          if (get(authorState, `lob.smb`, false))
            authorCurrentUserStatesAndLobs.push("smb")
          if (get(authorState, `lob.tro`, false))
            authorCurrentUserStatesAndLobs.push("tro")

          mappedCurrentUserState =
            USER_STATE_TO_AEM_STATE_MAPPING[
              authorCurrentUserStatesAndLobs.sort()
            ]

          if (mappedCurrentUserState === stateToMatch) break
        }
      }

      return (
        <div
          style={{
            display: shouldShow ? "block" : "none",
          }}
        >
          <WrappedComponent
            {...props}
            userStatesAndLobs={authorUserStatesAndLobs}
            currentUserState={mappedCurrentUserState}
          />
        </div>
      )
    }

    const [isMatch, matchedState] = isMatchedState(
      userStatesAndLobs,
      // Original user state implementation with just Journey (e.g. cookied)
      userState,
      // Newer user state implementation with Journey and LOB (e.g. tro).
      currentUserState,
      cqType,
    )

    const userStateArrayWithLobs = []
    if (isNumber(matchedState)) {
      userStateArrayWithLobs.push(userStatesAndLobs[matchedState].userState)
      if (userStatesAndLobs[matchedState].isOmr)
        userStateArrayWithLobs.push("omr")
      if (userStatesAndLobs[matchedState].isSmb)
        userStateArrayWithLobs.push("smb")
      if (userStatesAndLobs[matchedState].isTro)
        userStateArrayWithLobs.push("tro")
    }

    setLocalStorageUserState({
      currentUserState,
      userStatesAndLobs: userStateArrayWithLobs,
    })

    return userStatesAndLobs.length && isMatch ? (
      <WrappedComponent
        {...props}
        userStatesAndLobs={userStateArrayWithLobs}
        currentUserState={currentUserState}
      />
    ) : null
  }

  // Note: React 19 no-longer supports propTypes. Leaving this for reference.
  // WrappedComponentWithAuthentication.propTypes = {
  //   cqType: PropTypes.string,
  //   cmsContent: PropTypes.object,
  //   /* Current user state. */
  //   userState: PropTypes.number.isRequired,
  //   isAuthInitialized: PropTypes.bool,
  //   authorState: PropTypes.oneOfType([PropTypes.object, PropTypes.number])
  //     .isRequired,
  //   /* User states the parent component/AEM author specified that this component should be displayed for. */
  //   userStatesAndLobs: PropTypes.array,
  //   /* Current lob state in redux. */
  //   customerSelections: PropTypes.object.isRequired,
  //   userManageAccount: PropTypes.object.isRequired,
  // }

  // Note: React 19 no-longer supports defaultProps. Leaving this for reference.
  // WrappedComponentWithAuthentication.defaultProps = {
  //   userStatesAndLobs: [],
  //   cqType: "",
  //   cmsContent: {},
  //   isAuthInitialized: true, // only used in wm-next, ssr loading is too fast, causes issues with userState logic
  // }

  return connect(
    mapStateToProps,
    null,
  )(withUserState(WrappedComponentWithAuthentication))
}
